import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';
import listService from 'ListService';
import { generateControlsTreeData } from './TranslationUtils';

const TreeWrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 1.2rem;
  color: #333;
  padding: 10px;
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 15px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px 8px 30px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1.2rem;
  font-family: 'Poppins', sans-serif!important;
  &:focus {
    outline: none;
    border-color: #85B33E;
    box-shadow: 0 0 0 2px rgba(133, 179, 62, 0.2);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
  padding-top: 5px;
`;

const TreeList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
  overflow-y: auto;
  flex-grow: 1;
`;

const NodeItem = styled.li`
  /* Spacing handled by NodeContent padding */
`;

const NodeContent = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  margin-left: ${props => props.level * 20}px; /* Indentation */
  cursor: pointer;
  border-radius: 3px;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #e9e9e9;
  }

  ${props =>
        props.isSelected && css`
      background-color: #ECECEC; /* Highlight for selected item */
      color: #7A9F3F;
      font-weight: 500;
    `}
  
  ${props =>
        !props.isProperty && css`
      font-weight: 500;
    `}

    ${props =>
        props.isPage && css`
      font-weight: bold;
    `}
`;

const ToggleIcon = styled.i`
  margin-right: 6px;
  font-size: 1rem;
  display: inline-block;
  width: 1em; 
  text-align: center; 
  color: #555; 
  cursor: pointer;
  user-select: none;

`;

const NodeLabel = styled.span`
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilterWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 2px;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    font-size: 1rem;
    color: #333;
    cursor: pointer;

    input {
        width: 14px;
        height: 14px;
    }

    span{
        padding: 4px 4px 0 0;
    }
`;


const TreeNode = ({ node, level, onSelect, selectedId, expandedNodes, onToggleExpand }) => {
    const isSelected = node.id === selectedId;
    const isExpanded = expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;

    const handleSelect = () => {
        // Only properties are selectable
        if (node.isProperty) {
            onSelect(node.id, node);
        }
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        if (hasChildren) {
            onToggleExpand(node.id);
        }
    };

    // Allow selecting nodes without children, but toggling nodes that do.
    const clickAction = hasChildren ? handleToggle : handleSelect;

    return (
        <NodeItem>
            <NodeContent
                level={level}
                isSelected={isSelected}
                isProperty={node.isProperty}
                isPage={node.isPage}
                onClick={clickAction}
            >
                {hasChildren ? (
                    <ToggleIcon
                        className={`fa ${isExpanded ? 'pf-carot-down' : 'pf-carot-left pf-rotate-icon'}`}
                        onClick={handleToggle}
                        aria-expanded={isExpanded}
                        aria-hidden="true"
                    />
                ) : (
                    // Render a placeholder for alignment if no children (and thus no icon)
                    <span style={{ marginRight: '6px', display: 'inline-block', width: '1em' }}></span>
                )}
                <NodeLabel title={node.name}>{node.name}</NodeLabel>
            </NodeContent>
            {hasChildren && isExpanded && (
                <TreeList style={{ paddingLeft: 0 }}>
                    {node.children.map(childNode => (
                        <TreeNode
                            key={childNode.id}
                            node={childNode}
                            level={level + 1}
                            onSelect={onSelect}
                            selectedId={selectedId}
                            expandedNodes={expandedNodes}
                            onToggleExpand={onToggleExpand}
                        />
                    ))}
                </TreeList>
            )}
        </NodeItem>
    );
};

// --- Main ControlsTree Component ---
const ControlsTree = ({ onNodeSelect, defaultSelectedId = null, defaultSelectedAttribute = null, pageCollection }) => {
    const [treeData, setTreeData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedId, setSelectedId] = useState(defaultSelectedId);
    const [expandedNodes, setExpandedNodes] = useState({});
    const [showEmptyDefaults, setShowEmptyDefaults] = useState(false);

    // Generate tree data using the new utility function on component mount
    useEffect(() => {
        if (pageCollection) {
            const data = generateControlsTreeData(listService, pageCollection, showEmptyDefaults);
            setTreeData(data);

            // Automatically expand all top-level page nodes by default
            const initialExpanded = {};
            data.forEach(pageNode => {
                if (pageNode.isPage) {
                    initialExpanded[pageNode.id] = true;
                }
            });
            setExpandedNodes(initialExpanded);
        } else {
            setTreeData([]);
            setExpandedNodes({});
        }
    }, [showEmptyDefaults]);

    const handleSelectNode = useCallback((id, nodeData) => {
        if (!onNodeSelect || !nodeData.isProperty) return;

        let defaultText = "";
        
        // If the selected node has a columnId, it's a column property.
        if (nodeData.columnId) {
            // obj is the table's ID
            const tableControl = listService.getObject(nodeData.obj);
            if (tableControl) {
                const gridData = tableControl.model.get("gridData") || [];
                // Find the specific column in the gridData array
                const column = gridData.find(c => c.id === nodeData.columnId);
                if (column) {
                    defaultText = column.get(nodeData.key) || "";
                }
            }
        } else {
            // Otherwise, it's a regular control or page property.
            let obj;
            if (nodeData.obj === "app") {
                obj = listService.getObject("aForm");
            } else if (nodeData.parent === "app") {
                obj = listService.getPage(nodeData.obj);
            } else {
                obj = listService.getObject(nodeData.obj);
            }

            if (obj && obj.model) {
                defaultText = obj.model.get(nodeData.key) || "";
            }
        }

        const enrichedNodeData = {
            ...nodeData,
            defaultText
        };

        setSelectedId(id);
        onNodeSelect(id, enrichedNodeData);
    }, [onNodeSelect]);

    // --- Handle the initial selection ---
    useEffect(() => {
        if (!defaultSelectedId || !defaultSelectedAttribute || treeData.length === 0) return;

        const findNodeAndPath = (nodes, id, currentPath = []) => {
            for (const node of nodes) {
                const path = [...currentPath, node.id];
                if (node.id === id) {
                    return { node, path };
                }
                if (node.children) {
                    const found = findNodeAndPath(node.children, id, path);
                    if (found) return found;
                }
            }
            return null;
        };

        // Find the control node that matches the provided ID and attribute
        const controlSearchResult = findNodeAndPath(treeData, defaultSelectedId);

        if (controlSearchResult) {
            const { node: controlNode, path: controlPath } = controlSearchResult;
            
            // Find the specific property within that control that matches the attribute key.
            const targetProperty = controlNode.children?.find(child => child.isProperty && child.key === defaultSelectedAttribute);

            if (targetProperty) {
                setExpandedNodes(prev => {
                    const newExpanded = { ...prev };
                    controlPath.forEach(nodeId => { newExpanded[nodeId] = true; });
                    return newExpanded;
                });

                // select the target property.
                handleSelectNode(targetProperty.id, targetProperty);
            }
        }
    }, [treeData, defaultSelectedId, handleSelectNode]);
    
    const handleToggleExpand = useCallback((nodeId) => {
        setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
    }, []);

    const filterTree = useCallback((nodes, term) => {
        if (!term.trim()) {
            return nodes;
        }
        const lowerCaseTerm = term.toLowerCase();

        return nodes.reduce((acc, node) => {
            const nodeNameMatches = node.name.toLowerCase().includes(lowerCaseTerm);

            if (nodeNameMatches) {
                acc.push(node);
                return acc;
            }

            // If the parent does not match, we check if any of its children do.
            const children = node.children || [];
            if (children.length > 0) {
                const filteredChildren = filterTree(children, term);
                // If there are any matching children, we keep the parent but
                // only with the children that passed the filter.
                if (filteredChildren.length > 0) {
                    acc.push({ ...node, children: filteredChildren });
                }
            }
            
            return acc;
        }, []);
    }, []);

    const filteredData = useMemo(() => filterTree(treeData, searchTerm), [treeData, searchTerm, filterTree]);

    return (
        <TreeWrapper>
            <FilterWrapper>
                <CheckboxLabel>
                <span>Show properties with no default value</span>
                    <input 
                        type="checkbox" 
                        checked={showEmptyDefaults} 
                        onChange={() => setShowEmptyDefaults(prev => !prev)} 
                    />
                    
                </CheckboxLabel>
            </FilterWrapper>
            <SearchInputWrapper>
                {/* Search input UI */}
                <SearchInput
                    type="text"
                    placeholder="Search Objects..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </SearchInputWrapper>
            <TreeList>
                {filteredData.map(node => (
                    <TreeNode
                        key={node.id}
                        node={node}
                        level={0}
                        onSelect={handleSelectNode}
                        selectedId={selectedId}
                        expandedNodes={expandedNodes}
                        onToggleExpand={handleToggleExpand}
                    />
                ))}
            </TreeList>
        </TreeWrapper>
    );
};

export default ControlsTree;